import * as React from "react";
import { useTranslation } from "react-i18next";
import { LoginPage } from "../../page/login-page";
import useMakeSFRequest from "../../request/salesforce/make-sf-call";
import { AppLoader } from "../app-loader";
import { useAuth } from "./firebase-context";

type AuthGuardProps = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: AuthGuardProps) {
  const { t } = useTranslation();
  const { user, initialized, updateUserProfile } = useAuth();

  //NOTE: "Preemptive refresh" is here to prevent attempting to refresh many times simultaneously.
  //      Previously this would happen at startup, because ~5 calls can go out (e.g. one-time fetches
  //      for things like groups and listings).

  const sfRequest = useMakeSFRequest();
  const [sessionReady, setSessionReady] = React.useState(false);
  const [sessionPending, setSessionPending] = React.useState(false);

  const initializeSession = React.useCallback(
    async () => {
      if (!user) {
        console.warn('attempting to initialize session without a user');
        return;
      }
      if (sessionReady) {
        console.warn('redundant attempt to initialize session');
        return;
      }
      if (sessionPending) {
        return;
      }
      setSessionPending(true);

      const refreshResponse = await sfRequest(
        { endpointPath: 'unused', method: 'GET' },
        true
      );
      if (refreshResponse.error) {
        console.warn('preemptive refresh received an error', refreshResponse.error);
      } else if (refreshResponse.status !== 200) {
        console.warn('preemptive refresh failed', refreshResponse.status);
      }

      const profileResponse = await sfRequest({
        endpointPath: `/api/user/${user.sf_user_id}`,
        method: 'GET',
      });

      if (profileResponse.status === 200 && (profileResponse as any).user) {
        const apiUser = (profileResponse as any).user as ApiUser;
        await updateUserProfile({
          ...apiUser,
          sf_user_id: user.sf_user_id,
          permissions: apiUser.permissions,
        });
      } else if (profileResponse.error) {
        console.warn('session user profile sync failed', profileResponse.error);
      }

      setSessionReady(true);
      setSessionPending(false);
    },
    [sessionPending, sessionReady, sfRequest, updateUserProfile, user]
  );

  React.useEffect(() => {
    if (!user && sessionReady) {
      setSessionReady(false);
    }
  }, [user, sessionReady]);

  if (!initialized) {
    return (
      <AppLoader message={t("auth_loader_loading")} />
    );
  }

  if (!user) {
    return (<LoginPage />);
  }

  if (!sessionReady) {
    initializeSession();
    return (
      <AppLoader message={t("auth_loader_sf_oauth")} />
    );
  }

  return (<> {children} </>);
}
